import {
  createSceneLoadQueue,
  prepareSceneImages,
} from './scene-image-preparation.js';
import { SceneTextureCache } from './scene-texture-cache.js';

export const DEFAULT_PREWARM_TIMEOUT = 1200;

export class HeroImagePrewarm {
  constructor(options = {}) {
    this.textureCacheFactory =
      options.textureCacheFactory ?? (() => new SceneTextureCache());
    this.state = null;
  }

  prewarm(sources = [], profile = {}, options = {}) {
    const sceneImages = prepareSceneImages(sources, profile);

    if (!sceneImages.length) {
      this.dispose();
      return Promise.resolve(false);
    }

    const signature = createPrewarmSignature(sources, profile);

    if (!this.state || this.state.signature !== signature) {
      this.dispose();
      const textureCache = this.textureCacheFactory();
      const queue = createSceneLoadQueue(sceneImages, profile);
      const initialPromise = Promise.allSettled(
        queue.initial.map((item) => textureCache.load(item.resource))
      ).then(() => true);

      this.state = {
        signature,
        textureCache,
        initialPromise,
      };
    }

    this.state.timedPromise = withTimeout(
      this.state.initialPromise,
      options.timeout ?? DEFAULT_PREWARM_TIMEOUT
    );

    return this.state.timedPromise;
  }

  consume(sources = [], profile = {}) {
    const signature = createPrewarmSignature(sources, profile);

    if (!this.state || this.state.signature !== signature) {
      return null;
    }

    const textureCache = this.state.textureCache;
    this.state = null;
    return textureCache;
  }

  dispose() {
    this.state?.textureCache?.dispose?.();
    this.state = null;
  }
}

export const floemaHeroPrewarm = new HeroImagePrewarm();

export function createPrewarmSignature(sources = [], profile = {}) {
  return JSON.stringify({
    sources: sources.map((source) => ({
      src: source.src,
      fallbackSrc: source.fallbackSrc,
      webp: source.webp,
      srcset: source.srcset,
      sizes: source.sizes,
    })),
    profile: normalizeSignatureValue(profile),
  });
}

function withTimeout(promise, timeout) {
  const timeoutMs = Math.max(0, Math.floor(Number(timeout) || 0));

  if (!timeoutMs) {
    return promise;
  }

  return Promise.race([
    promise,
    new Promise((resolve) => {
      setTimeout(() => resolve(false), timeoutMs);
    }),
  ]);
}

function normalizeSignatureValue(value) {
  if (Array.isArray(value)) {
    return value.map(normalizeSignatureValue);
  }

  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((result, key) => {
        const normalized = normalizeSignatureValue(value[key]);

        if (typeof normalized !== 'undefined') {
          result[key] = normalized;
        }

        return result;
      }, {});
  }

  if (typeof value === 'function') {
    return undefined;
  }

  return value;
}
